'use client';

import {
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import type { DataSourceConfig, FormDSL } from '@commenda/types';
import { useMemo, useRef } from 'react';

type RepeatableModuleOption = {
  moduleId: string;
  label: string;
  fields: { fieldId: string; label: string }[];
};

/** The repeatable modules that run before this field, so their entries exist when it is answered. */
function useEarlierRepeatableModules(
  dsl: FormDSL | undefined,
  currentFieldId: string | undefined
): RepeatableModuleOption[] {
  return useMemo(() => {
    const modulesInOrder = (dsl?.steps ?? []).flatMap((step) =>
      (step.modules ?? []).map((formModule) => ({ step, formModule }))
    );

    const ownIndex = modulesInOrder.findIndex(({ formModule }) =>
      (formModule.components ?? []).some(
        (c) => c.fieldId && c.fieldId === currentFieldId
      )
    );
    const earlier =
      ownIndex === -1 ? modulesInOrder : modulesInOrder.slice(0, ownIndex);

    return earlier
      .filter(({ formModule }) => formModule.repeatable?.enabled)
      .map(({ step, formModule }) => ({
        moduleId: formModule.saveKey || formModule.id,
        label: `${step.title ?? 'Step'} — ${formModule.name ?? formModule.id}`,
        fields: (formModule.components ?? [])
          .filter((c) => !!c.fieldId)
          .map((c) => ({
            fieldId: c.fieldId as string,
            label: c.label || (c.fieldId as string),
          })),
      }));
  }, [dsl, currentFieldId]);
}

export function FormFieldSourceEditor({
  dataSource,
  onChange,
  dsl,
  currentFieldId,
}: {
  dataSource?: DataSourceConfig;
  onChange: (dataSource: DataSourceConfig | undefined) => void;
  dsl?: FormDSL;
  currentFieldId?: string;
}) {
  const modules = useEarlierRepeatableModules(dsl, currentFieldId);
  const config = dataSource?.formField;
  const selectedModule = modules.find((m) => m.moduleId === config?.moduleId);

  // `onChange` replaces the config rather than updating it, so two edits in one React batch
  // would both build on the same prop and the second would drop the first.
  const sentConfigRef = useRef(config);
  const lastPropRef = useRef(config);
  if (lastPropRef.current !== config) {
    lastPropRef.current = config;
    sentConfigRef.current = config;
  }

  const update = (updates: Partial<NonNullable<DataSourceConfig['formField']>>) => {
    const formField = {
      moduleId: sentConfigRef.current?.moduleId ?? '',
      labelFields: sentConfigRef.current?.labelFields ?? [],
      ...sentConfigRef.current,
      ...updates,
    };
    sentConfigRef.current = formField;
    onChange({ type: 'form-field', formField });
  };

  const toggleLabelField = (fieldId: string) => {
    const current = sentConfigRef.current?.labelFields ?? [];
    update({
      labelFields: current.includes(fieldId)
        ? current.filter((f) => f !== fieldId)
        : [...current, fieldId],
    });
  };

  if (modules.length === 0) {
    return (
      <p className="txt-small-regular text-text-tertiary">
        No repeatable module runs before this field, so there are no entries to
        offer. Add one in an earlier step first.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Entries from</Label>
        <Select
          value={config?.moduleId || undefined}
          onValueChange={(moduleId) => update({ moduleId, labelFields: [] })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a repeatable module" />
          </SelectTrigger>
          <SelectContent>
            {modules.map((m) => (
              <SelectItem key={m.moduleId} value={m.moduleId}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedModule && (
        <div className="space-y-2">
          <Label>Build each option label from</Label>
          <div className="space-y-2">
            {selectedModule.fields.map((field) => (
              <label
                key={field.fieldId}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Checkbox
                  checked={(config?.labelFields ?? []).includes(field.fieldId)}
                  onCheckedChange={() => toggleLabelField(field.fieldId)}
                />
                <span className="txt-small-regular">{field.label}</span>
              </label>
            ))}
          </div>
          <p className="txt-small-regular text-text-tertiary">
            Ticked fields are joined in order to label each entry.
          </p>
        </div>
      )}

      <div className="space-y-2">
        <Label>Label when an entry is still blank</Label>
        <Input
          value={config?.emptyLabel ?? ''}
          placeholder="Entry 1, Entry 2, ..."
          onChange={(e) => update({ emptyLabel: e.target.value })}
        />
      </div>
    </div>
  );
}
